import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { DownloadService } from '../../../service/download.service';
import { HighlightService } from '../../../service/highlight.service';
import { IHighlightDTO } from '../../../dto/highlight/highlight.dto';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzResultComponent } from 'ng-zorro-antd/result';
import { NzTypographyComponent } from 'ng-zorro-antd/typography';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzListComponent } from 'ng-zorro-antd/list';
import { ILightUserDTO } from '../../../dto/user/light-user.dto';
import { IHighlightItemDTO } from '../../../dto/highlight/highlight-item.dto';
import { GalleryItem } from 'lightgallery/lg-utils';
import { IPictureHighlightItemDTO } from '../../../dto/highlight/picture-highlight-item.dto';
import { IVideoHighlightItemDTO } from '../../../dto/highlight/video-highlight-item.dto';
import { MediaType } from '../../../dto/enum/media-type.enum';
import { LightGallery } from 'lightgallery/lightgallery';
import lightGallery from 'lightgallery';
import {
  defaultLightGallerySettings,
  defaultVideoMaxSize,
} from '../../../config/light-gallery';
import { NzMessageService } from 'ng-zorro-antd/message';
import { MENTION_SYMBOL } from '../../../util/const';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { AnalyticsService } from '../../../service/analytics.service';
import { IUserDTO } from '../../../dto/user/user.dto';
import { SentryService } from '../../../service/sentry.service';
import { unsubscribe } from '../../../util/rx.util';
import { ComponentState } from '../../../dto/enum/component-state.enum';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-highlights',
  imports: [
    NzRowDirective,
    NzColDirective,
    NzSpinComponent,
    NzResultComponent,
    NzTypographyComponent,
    NzListComponent,
    NzTooltipDirective,
    NzIconDirective,
    NzImageModule,
  ],
  templateUrl: './highlights.component.html',
  styleUrl: './highlights.component.less',
})
export class HighlightsComponent implements OnInit, OnDestroy {
  public readonly userDTO: InputSignal<IUserDTO> = input.required<IUserDTO>();
  public readonly ComponentState: typeof ComponentState = ComponentState;
  public readonly state: WritableSignal<ComponentState> =
    signal<ComponentState>(ComponentState.LOADING);
  public readonly highlightDTOs: WritableSignal<IHighlightDTO[]> = signal<
    IHighlightDTO[]
  >([]);
  public readonly loadingHighlight: WritableSignal<boolean> =
    signal<boolean>(false);
  private readonly router: Router = inject(Router);
  private readonly sentryService: SentryService = inject(SentryService);
  private readonly downloadService: DownloadService = inject(DownloadService);
  private readonly highlightService: HighlightService =
    inject(HighlightService);
  private readonly nzMessageService: NzMessageService =
    inject(NzMessageService);
  private readonly analyticsService: AnalyticsService =
    inject(AnalyticsService);
  private readonly document: Document = inject<Document>(DOCUMENT);
  private highlightSub$?: Subscription = undefined;
  private highlightsSub$?: Subscription = undefined;
  private lightGallery?: LightGallery = undefined;

  public ngOnInit(): void {
    this.highlightsSub$ = this.highlightService.fetchHighlights().subscribe({
      next: (highlightDTOs: IHighlightDTO[]): void => {
        this.highlightDTOs.update((value: IHighlightDTO[]): IHighlightDTO[] => {
          value.push(...highlightDTOs);
          return value;
        });
        this.state.set(ComponentState.OK);
      },
      error: (error: HttpErrorResponse): void => {
        this.state.set(
          401 === error.status
            ? ComponentState.UNAUTHORIZED_ERROR
            : ComponentState.UNEXPECTED_ERROR
        );
        console.error('An error occurred during fetching highlights', error);
        if (401 !== error.status) {
          this.sentryService.captureException({
            error,
            severityLevel: 'error',
            transactionName: 'FETCH_HIGHLIGHTS_ERROR',
            user: {
              username: this.userDTO().username,
            },
          });
        }
      },
    });
  }

  public getDownloadUrl(fileUrl: string): string {
    return this.downloadService.getDownloadUrl(fileUrl);
  }

  public ngOnDestroy(): void {
    unsubscribe(this.highlightsSub$);
    unsubscribe(this.highlightSub$);
    this.closeLightGallery();
  }

  public onImageClick(highlightDTO: IHighlightDTO): void {
    if (this.loadingHighlight()) {
      return;
    }
    this.loadingHighlight.set(true);
    const messageId: string = this.nzMessageService.loading(
      $localize`:@@loading:Loading...`
    ).messageId;
    this.highlightSub$ = this.highlightService
      .fetchHighlightByPrimaryKey(highlightDTO.primaryKey)
      .subscribe({
        next: (highlightDTO: IHighlightDTO): void => {
          const galleryItems: GalleryItem[] = highlightDTO.items
            .filter(
              (highlightItemDTO: IHighlightItemDTO): boolean =>
                MediaType.PICTURE === highlightItemDTO.mediaType ||
                MediaType.VIDEO === highlightItemDTO.mediaType
            )
            .map(
              (highlightItemDTO: IHighlightItemDTO): GalleryItem =>
                MediaType.PICTURE === highlightItemDTO.mediaType
                  ? this.ofPictureHighlightItemDTO(highlightItemDTO)
                  : this.ofVideoHighlightItemDTO(highlightItemDTO)
            );
          let videoMaxSize: string | undefined = undefined;
          if (
            galleryItems.find(
              (galleryItem: GalleryItem): boolean => !!galleryItem.video
            )
          ) {
            videoMaxSize = defaultVideoMaxSize;
          }
          this.lightGallery = lightGallery(this.document.body, {
            ...defaultLightGallerySettings,
            dynamicEl: galleryItems,
            videoMaxSize,
          });

          const username: string = this.userDTO().username;

          this.document.body.addEventListener(
            'lgAfterClose',
            (): void => {
              this.lightGallery!.destroy();
              this.lightGallery = undefined;
              this.analyticsService.event('gallery_exec', {
                action: 'close',
                content: 'highlight',
                user: username,
                path: this.router.url,
              });
            },
            { once: true }
          );

          this.document.body.addEventListener(
            'lgAfterOpen',
            (): void => {
              this.analyticsService.event('gallery_exec', {
                action: 'open',
                content: 'highlight',
                user: username,
                path: this.router.url,
              });
            },
            { once: true }
          );

          this.document.body.addEventListener(
            'lgUserClick',
            (event: Event): void => {
              this.closeLightGallery();
              // @ts-ignore
              const username: string = event.detail.username;
              this.router.navigate(['/profiles', username]).then((): void => {
                this.analyticsService.event('user_exec', {
                  origin: 'highlight',
                  username: username,
                  path: this.router.url,
                });
              });
            },
            { once: true }
          );

          if (this.document.defaultView) {
            this.document.defaultView.addEventListener(
              'popstate',
              (): void => this.closeLightGallery(),
              { once: true }
            );
          }

          this.lightGallery.openGallery();
          this.loadingHighlight.set(false);
          this.nzMessageService.remove(messageId);
        },
        error: (error: HttpErrorResponse): void => {
          this.loadingHighlight.set(false);
          this.nzMessageService.remove(messageId);
          if (401 === error.status) {
            this.state.set(ComponentState.UNAUTHORIZED_ERROR);
          } else {
            this.nzMessageService.error(
              $localize`:@@page-profile-content-highlight-unexpected-error:Can't load highlight items. Please, try again or come back later`
            );
          }
          console.error(
            `An error occurred during fetching highlight [${highlightDTO.primaryKey}]`,
            error
          );
          if (401 !== error.status) {
            const userDTO: IUserDTO = this.userDTO();
            this.sentryService.captureException({
              error,
              severityLevel: 'error',
              transactionName: 'FETCH_HIGHLIGHT_ERROR',
              user: {
                id: userDTO.primaryKey,
                username: userDTO.username,
              },
            });
          }
        },
      });
  }

  private ofPictureHighlightItemDTO(
    highlightItemDTO: IHighlightItemDTO
  ): GalleryItem {
    const pictureHighlightItemDTO: IPictureHighlightItemDTO =
      highlightItemDTO as IPictureHighlightItemDTO;
    const pictureUrl: string = this.downloadService.getDownloadUrl(
      pictureHighlightItemDTO.pictureUrl
    );
    return {
      id: pictureHighlightItemDTO.primaryKey,
      mediaType: pictureHighlightItemDTO.mediaType,
      src: pictureUrl,
      downloadUrl: pictureUrl,
      thumb: pictureUrl,
      alt: pictureHighlightItemDTO.primaryKey,
      subHtml: this.getSubHtml(pictureHighlightItemDTO),
    };
  }

  private ofVideoHighlightItemDTO(
    highlightItemDTO: IHighlightItemDTO
  ): GalleryItem {
    const videoHighlightItemDTO: IVideoHighlightItemDTO =
      highlightItemDTO as IVideoHighlightItemDTO;
    const pictureUrl: string = this.getDownloadUrl(
      videoHighlightItemDTO.pictureUrl
    );
    const videoUrl: string = this.downloadService.getDownloadUrl(
      videoHighlightItemDTO.videoUrl
    );
    return {
      id: videoHighlightItemDTO.primaryKey,
      mediaType: videoHighlightItemDTO.mediaType,
      downloadUrl: videoUrl,
      thumb: pictureUrl,
      poster: pictureUrl,
      video: {
        source: [{ src: videoUrl, type: 'video/mp4' }],
        tracks: [],
        // @ts-ignore
        attributes: {
          preload: 'metadata',
          playsInline: true,
          controls: true,
        },
      },
      subHtml: this.getSubHtml(videoHighlightItemDTO),
    };
  }

  private getSubHtml(highlightItemDTO: IHighlightItemDTO): string {
    const lightUserDTOs: ILightUserDTO[] = highlightItemDTO.mentions;
    const date: string = new Date(highlightItemDTO.createdAt).toLocaleString();
    let subHtml: string = '<div class="sub-html">';
    subHtml += `<div class="caption">${date}</div>`;
    if (lightUserDTOs.length) {
      subHtml += '<div>';
      subHtml += lightUserDTOs
        .map(
          (lightUserDTO: ILightUserDTO): string =>
            `<a onclick="document.body.dispatchEvent(new CustomEvent('lgUserClick', {detail: {username: '${lightUserDTO.username}'}}))" class="user">${MENTION_SYMBOL + lightUserDTO.username}</a>`
        )
        .join('');
      subHtml += '</div>';
    }
    subHtml += '</div>';
    return subHtml;
  }

  private closeLightGallery(): void {
    if (this.lightGallery && this.lightGallery.lgOpened) {
      this.lightGallery.closeGallery();
    }
  }
}
